import { reverse } from 'named-urls'
import type { ComponentProps } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import type { TableData } from '@fourthwall/components'
import {
  CheckboxIndeterminate,
  List,
  TableSortableUI,
  TableUI,
  useMediaQueryContext,
} from '@fourthwall/components'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { routing } from '@utils/routing'
import { getProductLink } from '@utils/routing/getProductLink'

import type { Product } from '../../types'
import { ProductListItem } from './components/ProductListItem'
import { useProductsTableColumns } from './hooks/useProductsTableColumns'

export const ProductsData = ({
  products,
  currentPage,
  isLoading,
  onArchiveSelectedOffersClick,
  onDuplicateOffer,
  onArchiveOffer,
  onUnarchiveOffer,
  onProductPositionSet,
  onProductPositionChange,
  onToggleMultipleOfferStatus,
}: {
  products: Product[]
  currentPage: number
  isLoading: boolean
  onArchiveSelectedOffersClick: (offerIds: string[], cb?: () => void) => void
  onToggleMultipleOfferStatus: (offerIds: string[], cb?: () => void) => void
  onDuplicateOffer: (rowData: TableData) => void
  onArchiveOffer: (rowData: TableData) => void
  onUnarchiveOffer: (rowData: TableData) => void
  onProductPositionSet: (rowData: TableData) => void
  onProductPositionChange: (oldIndex: number, newIndex: number) => void
}) => {
  const media = useMediaQueryContext()
  const [rowSelection, setRowSelection] = useState({})
  const { hasPermission } = useDashboardPermissions()

  const { activeId, listItemsOrdered, dndContextProps, sortableContextProps } =
    TableSortableUI.useTableSortable({
      list: products,
      onPositionChange: onProductPositionChange,
    })

  useEffect(() => {
    setRowSelection({})
  }, [currentPage])

  const columns = useProductsTableColumns({
    onArchiveSelectedOffersClick: (offerIds) => {
      onArchiveSelectedOffersClick(offerIds, () => {
        setRowSelection({})
      })
    },
    onToggleMultipleOfferStatus: (offerIds) => {
      onToggleMultipleOfferStatus(offerIds, () => {
        setRowSelection({})
      })
    },
  })

  const table = useReactTable({
    data: listItemsOrdered,
    columns,
    state: {
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
  })

  const selectedRow = useMemo(() => {
    if (!activeId) {
      return null
    }

    return table.getRowModel().rows.find(({ original }) => original.id === activeId)
  }, [activeId, table])

  const getDropdownItems = (
    product: Product,
  ): ComponentProps<typeof ProductListItem>['dropdownItems'] => [
    {
      label: 'Change position',
      onClick: (tableData: TableData) => {
        onProductPositionSet(tableData)
      },
    },
    {
      label: 'Duplicate',
      enabled: product.options.canBeDuplicated && hasPermission('products.duplicate'),
      onClick: (tableData: TableData) => {
        onDuplicateOffer(tableData)
      },
    },
    {
      enabled: hasPermission('products.archive'),
      label: product.status === 'ARCHIVED' ? 'Unarchive' : 'Archive',
      onClick: (tableData: TableData) => {
        product.status === 'ARCHIVED' ? onUnarchiveOffer(tableData) : onArchiveOffer(tableData)
      },
    },
  ]

  if (!media.tablet) {
    return (
      <List
        data={products}
        isLoading={isLoading}
        getItemLink={(product: Product) => {
          return {
            pathname:
              product.type === 'COMBINED'
                ? reverse(routing.products.combined.combinedId.self, { combinedId: product.id })
                : reverse(routing.products.all.product.self, { productId: product.id }),
            search: location.search,
          }
        }}
        renderItem={(product: Product) => (
          <ProductListItem product={product} dropdownItems={getDropdownItems(product)} />
        )}
      />
    )
  }

  return (
    <TableUI.Root hoverable minWidth="600px">
      <TableUI.Head>
        {table
          .getHeaderGroups()
          .filter((header) => {
            if (table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) {
              // NOTE: there is no nice way to access the header group other than checking the depth or id
              // NOTE: we want to render only the first header group if there are selected rows
              return header.depth === 0
            }

            // NOTE: otherwise we want to render only the second header group
            return header.depth === 1
          })
          .map((headerGroup) => {
            return (
              <TableUI.HeadTr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableUI.HeadTh
                      key={header.id}
                      colSpan={header.colSpan}
                      textAlign={header.column.columnDef.meta?.textAlign}
                      width={header.column.columnDef.meta?.width}
                      position={header.column.columnDef.meta?.position}
                      verticalSpacing="6px"
                    >
                      {header.isPlaceholder ? null : (
                        <>{flexRender(header.column.columnDef.header, header.getContext())}</>
                      )}
                    </TableUI.HeadTh>
                  )
                })}
              </TableUI.HeadTr>
            )
          })}
      </TableUI.Head>

      {
        // eslint-disable-next-line no-nested-ternary
        products.length > 0 ? (
          <TableSortableUI.DndContext {...dndContextProps}>
            <TableSortableUI.SortableContext {...sortableContextProps}>
              <TableSortableUI.Body isLoading={isLoading}>
                {table.getRowModel().rows.map((row) => {
                  return (
                    <TableSortableUI.BodyTr
                      key={row.original.id}
                      id={row.original.id}
                      isSelected={row.getIsSelected()}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const isLeftPlaceholder =
                          cell.column.columnDef.id === 'row-left-side-placeholder'

                        const shouldWorkAsSelectionToggle =
                          table.getIsSomeRowsSelected() ||
                          table.getIsAllRowsSelected() ||
                          isLeftPlaceholder

                        return (
                          <TableSortableUI.BodyTd
                            key={cell.id}
                            textAlign={cell.column.columnDef.meta?.textAlign}
                            width={cell.column.columnDef.meta?.width}
                            position={cell.column.columnDef.meta?.position}
                            onClick={() => {
                              if (isLeftPlaceholder) {
                                return
                              }

                              if (shouldWorkAsSelectionToggle) {
                                row.toggleSelected()
                              }
                            }}
                          >
                            {!shouldWorkAsSelectionToggle ? (
                              <Link
                                to={getProductLink(cell.row.original.type, cell.row.original.id)}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </Link>
                            ) : (
                              <>{flexRender(cell.column.columnDef.cell, cell.getContext())}</>
                            )}
                          </TableSortableUI.BodyTd>
                        )
                      })}

                      {table.getIsSomeRowsSelected() || table.getIsAllRowsSelected() ? null : (
                        <TableSortableUI.BodyTd
                          key="table-row-actions"
                          width="0px"
                          position="relative"
                        >
                          <TableUI.TableRowActions>
                            <>
                              {getDropdownItems(row.original)
                                .filter(({ enabled = true }) => enabled)
                                .map((action) => (
                                  <TableUI.TableRowActionsItem
                                    label={action.label}
                                    onClick={() => action.onClick(row.original)}
                                  />
                                ))}
                            </>
                          </TableUI.TableRowActions>
                        </TableSortableUI.BodyTd>
                      )}
                    </TableSortableUI.BodyTr>
                  )
                })}
              </TableSortableUI.Body>
            </TableSortableUI.SortableContext>

            <TableSortableUI.DragOverlay>
              {activeId ? (
                <TableUI.Root hoverable>
                  <TableUI.Body isLoading={false}>
                    <TableUI.BodyTr key={`overlay-${activeId}`} isDragged>
                      <TableSortableUI.BodyTd
                        key="left-placeholder"
                        width="0px"
                        position="relative"
                      >
                        <TableUI.RowSelection visible>
                          <CheckboxIndeterminate
                            checked={selectedRow?.getIsSelected()}
                            disabled={!selectedRow?.getCanSelect()}
                            indeterminate={selectedRow?.getIsSomeSelected()}
                            onChange={selectedRow?.getToggleSelectedHandler()}
                          />
                        </TableUI.RowSelection>
                      </TableSortableUI.BodyTd>

                      {selectedRow?.getVisibleCells().map((cell) => (
                        <TableUI.BodyTd
                          key={cell.id}
                          textAlign={cell.column.columnDef.meta?.textAlign}
                          width={cell.column.columnDef.meta?.width}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableUI.BodyTd>
                      ))}
                    </TableUI.BodyTr>
                  </TableUI.Body>
                </TableUI.Root>
              ) : null}
            </TableSortableUI.DragOverlay>
          </TableSortableUI.DndContext>
        ) : (
          <TableUI.Body isLoading={isLoading}>
            <tr>
              <TableUI.BodyTd colSpan={table.getAllLeafColumns().length}>
                <TableUI.Loader />
              </TableUI.BodyTd>
            </tr>
          </TableUI.Body>
        )
      }
    </TableUI.Root>
  )
}
